type StructuredToolError = {
  error: string;
  message?: string;
  secretName?: string;
  url?: string | null;
};

type ToolInvocationErrorSummary = {
  title: string;
  description: string;
  details?: string;
};

function isInsufficientScopeError(result: StructuredToolError): boolean {
  const errorText = result.error.toLowerCase();
  const messageText = result.message?.toLowerCase() ?? "";
  return (
    errorText.includes("forbidden") ||
    errorText.includes("insufficient_scope") ||
    messageText.includes("scope")
  );
}

function isStructuredToolError(result: unknown): result is StructuredToolError {
  return (
    typeof result === "object" &&
    result !== null &&
    "error" in result &&
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    typeof (result as { error?: unknown }).error === "string"
  );
}

export function isAuthToolError(
  result: unknown
): result is StructuredToolError {
  return (
    isStructuredToolError(result) &&
    (result.error === "auth_timeout" || result.error === "auth_failed")
  );
}

export function getAuthToolErrorMessage(result: StructuredToolError): string {
  if (result.error === "missing_secret" && result.secretName) {
    return `Missing secret "${result.secretName}". Add it in project secrets and try again.`;
  }

  if (result.error === "auth_timeout") {
    return "Authorization timed out. Retry authorization and run again.";
  }

  if (result.message) {
    return result.message;
  }

  return result.error;
}

function isDeniedToolResult(result: unknown): boolean {
  return (
    typeof result === "object" &&
    result !== null &&
    "denied" in result &&
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (result as { denied?: unknown }).denied === true
  );
}

export function getToolInvocationErrorSummary(
  result: unknown
): ToolInvocationErrorSummary | null {
  if (result === undefined) {
    return null;
  }

  if (isDeniedToolResult(result)) {
    return {
      title: "Action denied",
      description:
        isStructuredToolError(result) && result.message
          ? result.message
          : "Approval was denied before the tool could complete.",
    };
  }

  if (isStructuredToolError(result)) {
    if (result.error === "missing_secret") {
      return {
        title: "Missing required secret",
        description: getAuthToolErrorMessage(result),
      };
    }

    if (isInsufficientScopeError(result)) {
      return {
        title: result.message ?? "Insufficient permissions",
        description:
          "Reconnect an account with the required permissions to continue.",
        details: result.error,
      };
    }

    return {
      title: result.message ?? "Tool execution failed",
      description: "Check the response details below and try again.",
      details: result.error,
    };
  }

  return null;
}
